import { createFileRoute, Link, redirect, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { ClientResponseError } from 'pocketbase';
import { Sun, Moon } from 'lucide-react';
import { pb, isLoggedIn } from '~/lib/pb';
import { applyTheme, getStoredTheme } from '~/lib/theme';
import type { Theme } from '~/lib/theme';
import { PasswordInput } from '~/components/PasswordInput/PasswordInput';
import {
  consumeLogoutReason,
  LOGOUT_REASON_COPY,
  type LogoutReason,
} from '~/lib/session';
import styles from './login.module.css';

// Tras login, el destino depende del rol:
// - superadmin → /clientes (panel fleet)
// - client_admin / sales_rep → / (dashboard del tenant)
// El viewMode se deriva del path, así que /clientes implica modo admin
// automáticamente.
function postLoginDestination(): '/' | '/clientes' {
  const me = pb.authStore.record;
  return me?.role === 'superadmin' ? '/clientes' : '/';
}

/** Mensaje de login en español (PB responde en inglés por defecto). */
function loginErrorMessage(err: unknown): string {
  if (err instanceof ClientResponseError) {
    const status = err.status;
    // 400 = credenciales inválidas o usuario inexistente (PB no distingue).
    if (status === 400 || /failed to authenticate/i.test(err.message)) {
      return 'Email o contraseña incorrectos.';
    }
    if (status === 403) {
      if (/suspendido|ya no existe/i.test(err.message)) return err.message;
      return 'No tienes permiso para entrar.';
    }
    if (status >= 500) return 'El servidor no responde. Intenta de nuevo en un momento.';
  }
  return 'No se pudo iniciar sesión. Revisa tus datos e intenta de nuevo.';
}

export const Route = createFileRoute('/login')({
  validateSearch: (s: Record<string, unknown>) => ({
    reason:
      s.reason === 'session_expired' ||
      s.reason === 'password_changed' ||
      s.reason === 'tenant_suspended'
        ? (s.reason as LogoutReason)
        : undefined,
    redirect: typeof s.redirect === 'string' ? s.redirect : undefined,
  }),
  beforeLoad: () => {
    if (isLoggedIn()) {
      throw redirect({ to: postLoginDestination() });
    }
  },
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [theme, setTheme] = useState<Theme>(getStoredTheme());

  useEffect(() => {
    const reason = search.reason ?? consumeLogoutReason();
    if (reason) setInfo(LOGOUT_REASON_COPY[reason]);
  }, [search.reason]);

  function handleToggleTheme() {
    const next: Theme = theme === 'light' ? 'dark' : 'light';
    applyTheme(next);
    setTheme(next);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      // SOLO autenticamos contra `users` (cuenta de la app, con role=superadmin/
      // client_admin/sales_rep). El admin built-in de PocketBase (`_superusers`)
      // es otra cuenta y otra URL (api…/_/) — no se mezcla acá a propósito.
      await pb.collection('users').authWithPassword(email, password);
      void navigate({ to: postLoginDestination() });
    } catch (err) {
      setError(loginErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className={styles.login}>
      <button
        type="button"
        className={styles.login__themeToggle}
        onClick={handleToggleTheme}
        aria-label={`Cambiar a tema ${theme === 'light' ? 'oscuro' : 'claro'}`}
      >
        {theme === 'light' ? <Moon size={18} /> : <Sun size={18} />}
      </button>

      <div className={styles.login__panel}>
        <header className={styles.login__header}>
          <div className={styles.login__brand}>
            <img
              className={styles.login__brandMark}
              src="/brand/isotipo.png"
              alt="Life Focus"
              width={48}
              height={48}
            />
            <h1 className={styles.login__brandLabel}>Sistema Focus</h1>
          </div>
          <p className={`muted ${styles.login__subtitle}`}>
            Accede a tu CRM, automatizaciones y dashboard de ROI.
          </p>
        </header>

        <form className={styles.login__form} onSubmit={handleSubmit}>
          <label className={styles.field}>
            <span className={styles.field__label}>Email</span>
            <input
              className={styles.field__input}
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
            />
          </label>

          <label className={styles.field}>
            <span className={styles.field__label}>Contraseña</span>
            <PasswordInput
              className={styles.field__input}
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
            />
          </label>

          {info && !error && (
            <p className={styles.login__info} role="status">
              {info}
            </p>
          )}

          {error && (
            <p className={styles.login__error} role="alert">
              {error}
            </p>
          )}

          <button
            type="submit"
            className={`${styles.button} ${styles['button--primary']}`}
            disabled={loading}
          >
            {loading ? 'Entrando…' : 'Entrar'}
          </button>

          <div className={styles.login__forgotRow}>
            <Link to="/forgot-password" className={styles.login__forgotLink}>
              ¿Olvidaste tu contraseña?
            </Link>
          </div>
        </form>
      </div>

      <footer className={styles.login__footer}>
        <p className="subtle">© Life Focus. Todos los derechos reservados.</p>
      </footer>
    </div>
  );
}
